
import { useEffect, useRef } from 'react';

interface RecordingControlsProps {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function RecordingControls({
  isRecording,
  isPaused,
  duration,
  onStart,
  onPause,
  onResume,
  onStop,
}: RecordingControlsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const barsRef = useRef<number[]>(Array.from({ length: 40 }, () => 0.1));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barWidth = canvas.width / barsRef.current.length;
      const centerY = canvas.height / 2;

      barsRef.current.forEach((val, i) => {
        const height = val * centerY * 1.8;
        const x = i * barWidth + barWidth * 0.15;
        const w = barWidth * 0.7;

        const gradient = ctx.createLinearGradient(0, centerY - height, 0, centerY + height);
        gradient.addColorStop(0, isRecording && !isPaused ? '#14B8A6' : '#CBD5E1');
        gradient.addColorStop(1, isRecording && !isPaused ? '#0D9488' : '#94A3B8');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x, centerY - height, w, height * 2, 3);
        ctx.fill();
      });

      if (isRecording && !isPaused) {
        barsRef.current = barsRef.current.map((_, i) => {
          if (i === 0) return 0.1 + Math.random() * 0.9;
          return barsRef.current[i - 1] * 0.85 + Math.random() * 0.15;
        });
      } else {
        barsRef.current = barsRef.current.map(v => Math.max(0.05, v * 0.92));
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isRecording, isPaused]);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Waveform */}
      <div className="w-full bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <canvas ref={canvasRef} width={600} height={80} className="w-full h-16" />
      </div>

      {/* Timer */}
      <div className="flex items-center gap-3">
        {isRecording && (
          <span className={`w-3 h-3 rounded-full ${isPaused ? 'bg-amber-400' : 'bg-red-500 animate-pulse'}`}></span>
        )}
        <span className="text-4xl font-mono font-bold text-gray-800 tabular-nums">
          {formatDuration(duration)}
        </span>
        {isRecording && (
          <span className="text-sm font-medium text-gray-400">
            {isPaused ? 'Paused' : 'Recording...'}
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        {!isRecording ? (
          <button
            onClick={onStart}
            className="flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-2xl font-bold text-lg shadow-xl shadow-teal-500/30 hover:from-teal-600 hover:to-emerald-600 hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-mic-line text-2xl"></i>
            </div>
            Start Recording
          </button>
        ) : (
          <>
            <button
              onClick={isPaused ? onResume : onPause}
              className="flex items-center gap-2 px-6 py-3.5 bg-amber-100 text-amber-700 border-2 border-amber-200 rounded-xl font-semibold hover:bg-amber-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${isPaused ? 'ri-play-line' : 'ri-pause-line'} text-lg`}></i>
              </div>
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            <button
              onClick={onStop}
              className="flex items-center gap-2 px-6 py-3.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 hover:scale-105 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-red-500/30"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-stop-circle-line text-lg"></i>
              </div>
              Stop
            </button>
          </>
        )}
      </div>
    </div>
  );
}
